import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfigService } from '@fuse/services/config/config.service';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { ThemeSwitchComponent } from 'app/layout/common/themeswitch/themeswitch.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { SecurityService } from 'app/services/security.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'futuristic-layout',
    templateUrl: './futuristic.component.html',
    styleUrls: ['./futuristic.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgxLoadingBar,
        CommonModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatButtonModule,
        MatIconModule,
        FuseFullscreenComponent,
        RouterOutlet,
        ThemeSwitchComponent,
    ],
})
export class FuturisticLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: any;
    isDarkMode = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    currentTheme: any;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        // private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private fuseConfigService: FuseConfigService,
        private securityService: SecurityService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    toggleDropdown() {
        const dropdown = document.getElementById('feedbackDropdown');
        if (dropdown) {
            dropdown.classList.toggle('hidden');
            dropdown.classList.toggle('show');
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        const userRole = this.securityService.getUserRole();
        const userCompany = this.securityService.getUserCompany();
        console.log('User Role:', userRole);
        console.log('User Company:', userCompany);
        // Subscribe to navigation data
        this.navigation = {
            futuristic: [
                // {
                //     id: 'dashboards',
                //     title: 'Home',
                //     subtitle: '',
                //     type: 'group',
                //     icon: 'heroicons_outline:home',
                //     children:[{
                //         id: 'home',
                //         title: 'Home',
                //         type: 'basic',
                //         icon: 'heroicons_outline:home',
                //         link: '/home',
                //     },],
                // },
                {
                    id: 'dashboards',
                    title: 'Market Performance Benchmarking',
                    type: 'group',
                    icon: 'heroicons_outline:home',
                    hidden: () =>
                        !(
                            userCompany === 'Circle Bit' ||
                            userCompany === 'AgNexus' ||
                            userCompany === 'Zoetis'
                        ),
                    children: [
                        {
                            id: 'mpb',
                            title: 'On Segment Level',
                            type: 'basic',
                            icon: 'heroicons_outline:document-chart-bar',
                            link: '/mip/mpb',
                        },
                        {
                            id: 'product-performance',
                            title: 'On Product Level',
                            type: 'basic',
                            icon: 'heroicons_outline:chart-bar',
                            link: '/mip/product-performance',
                            hidden: () =>
                                !(
                                    userCompany === 'Circle Bit' ||
                                    userCompany === 'AgNexus' ||
                                    userCompany === 'Zoetis'
                                ),
                        },
                        {
                            id: 'dashboard',
                            title: 'On SKU Level',
                            type: 'basic',
                            icon: 'heroicons_outline:chart-pie',
                            link: '/mip/product-level-reporting',
                            hidden: () =>
                                !(
                                    userCompany === 'Circle Bit' ||
                                    userCompany === 'AgNexus' ||
                                    userCompany === 'Zoetis'
                                ),
                        },
                        {
                            id: 'mass-download',
                            title: 'Mass Download',
                            type: 'basic',
                            icon: 'heroicons_outline:arrow-down-tray',
                            link: '/mip/mass-download',
                            hidden: () =>
                                !(
                                    userCompany === 'Circle Bit' ||
                                    userCompany === 'AgNexus'
                                ),
                        },
                    ],
                },
                {
                    id: 'veterinary',
                    title: 'PowerBI',
                    type: 'group',
                    icon: 'heroicons_outline:chart-bar-square',
                    children: [
                        {
                            id: 'powerbi-report',
                            title: 'PowerBI Reports',
                            type: 'basic',
                            icon: 'heroicons_outline:presentation-chart-line',
                            link: '/powerbi/reports',
                        },
                        {
                            id: 'powerbi-attachments',
                            title: 'Attachments',
                            type: 'basic',
                            icon: 'heroicons_outline:paper-clip',
                            link: '/powerbi/attachments',
                        },
                    ],
                },
            ],
        };

        if (userCompany == 'Circle Bit' || userCompany == 'AgNexus') {
            this.navigation.futuristic.push({
                id: 'mst',
                title: 'Market Simulation Tool',
                type: 'group',
                icon: 'heroicons_outline:home',
                children: [
                    {
                        id: 'mst',
                        title: 'Market Simulation Dashboard',
                        type: 'basic',
                        icon: 'heroicons_outline:presentation-chart-line',
                        link: '/mst/mst-main',
                    },

                    {
                        id: 'galvmed',
                        title: 'Market Potential Tool',
                        type: 'basic',
                        icon: 'heroicons_outline:adjustments-vertical',
                        link: 'https://mpt.agnexus.africa/',
                        externalLink: true,
                        target: '_blank',
                    },
                    {
                        id: 'referenceData',
                        title: 'Reference Information',
                        type: 'collapsable',
                        icon: 'heroicons_outline:table-cells',
                        children: [
                            {
                                id: 'african-reference-info-system',
                                title: 'African Reference Information System',
                                type: 'basic',
                                icon: 'heroicons_outline:rectangle-group',
                                link: '/mst/african-reference-info',
                            },
                            {
                                id: 'african-disease-atlas',
                                title: 'African Disease Atlas',
                                type: 'basic',
                                icon: 'heroicons_outline:rectangle-group',
                                link: '/mst/disease-atlas',
                            },
                            {
                                id: 'vet-solution-guide',
                                title: 'Veterinary Solution Guide',
                                type: 'basic',
                                icon: 'heroicons_outline:rectangle-group',
                                link: '/mst/veterinary-solution-guide',
                            },
                            {
                                id: 'management-tools',
                                title: 'Management Tools',
                                type: 'basic',
                                icon: 'heroicons_outline:rectangle-group',
                                link: '/mst/management-tools',
                            },
                        ],
                    },
                    {
                        id: 'internal',
                        title: 'Internal Tools',
                        type: 'collapsable',
                        icon: 'heroicons_outline:wrench-screwdriver',
                        children: [
                            {
                                id: 'sensitivity',
                                title: 'Reference Variable Impact',
                                type: 'basic',
                                icon: 'heroicons_outline:chart-bar-square',
                                link: '/mst/ref-impact',
                            },
                            {
                                id: 'model-simulation',
                                title: 'Model Simulation',
                                type: 'basic',
                                icon: 'heroicons_outline:chart-pie',
                                link: '/mst/model-simulation',
                            },
                        ],
                    },
                ],
            });
        }

        this.navigation.futuristic.push({
            id: 'acc',
            title: 'Accounts, Settings & Training',
            type: 'group',
            icon: 'heroicons_outline:home',
            children: [
                {
                    id: 'users',
                    title: 'Settings',
                    type: 'basic',
                    icon: 'heroicons_outline:cog-6-tooth',
                    link: '/settings',
                },
            ],
        });

        // Subscribe to the user service
        this.user = this._userService.user$;

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        this.fuseConfigService.config$.subscribe((config) => {
            this.currentTheme = config.scheme;
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
